import { CorporateAccountApi } from '@lib/state/api/generated/reservations';
import { invoiceStatusStore, InvoiceStatusStore } from './invoice-status.store';
import { InvoiceStatusFilter, MarkInvoicesSentRequest } from './invoice-status.model';
import { defer, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { applyTransaction, setLoading } from '@datorama/akita';
import { dispatchForm, pollUntil, unsubscribeCallback } from '@lib/state';
import { InvoiceStatusForms } from './invoice-status.forms';

export class InvoiceStatusService {
  constructor(private api: CorporateAccountApi, private store: InvoiceStatusStore) {}

  pollInvoiceStatuses(
    {
      createdStartDate,
      createdEndDate,
      status,
      invoiceRunId,
      corporateAccountId,
      customerId,
      sortBy,
    }: InvoiceStatusFilter,
    continuePolling: boolean = false,
    interval: number = 5000
  ) {
    const sub = defer(() => {
      if (invoiceRunId)
        return this.api.corporateaccountInvoiceStatusesGet(
          createdStartDate?.toString(),
          createdEndDate?.toString(),
          status,
          invoiceRunId,
          corporateAccountId,
          customerId
        );
    })
      .pipe(
        map(data => data.data.data ?? []),
        pollUntil(interval, continuePolling)
      )
      .subscribe(s =>
        applyTransaction(
          () => this.store.set(s),
          this.updateUIState({
            createdStartDate,
            createdEndDate,
            status,
            invoiceRunId,
            corporateAccountId,
            sortBy,
          })
        )
      );

    return unsubscribeCallback(sub);
  }

  getInvoiceStatuses({
    createdStartDate,
    createdEndDate,
    status,
    invoiceRunId,
    corporateAccountId,
    customerId,
    sortBy,
  }: InvoiceStatusFilter) {
    const sub = defer(() => {
      return this.api.corporateaccountInvoiceStatusesGet(
        createdStartDate?.toString(),
        createdEndDate?.toString(),
        status,
        invoiceRunId,
        corporateAccountId,
        customerId
      );
    })
      .pipe(
        map(data => data.data.data ?? []),
        setLoading(this.store)
      )
      .subscribe(s =>
        applyTransaction(
          () => this.store.set(s),
          this.updateUIState({
            createdStartDate,
            createdEndDate,
            status,
            invoiceRunId,
            corporateAccountId,
            sortBy,
          })
        )
      );

    return unsubscribeCallback(sub);
  }

  markInvoiceSent(runId: string, request: MarkInvoicesSentRequest) {
    from(this.api.corporateaccountRunIdInvoiceSentPost(runId, request))
      .pipe(
        map(response => response.data.data),
        dispatchForm(InvoiceStatusForms.MarkInvoicesSentForm)
      )
      .subscribe(data =>
        applyTransaction(() => {
          this.store.upsertMany(data);
        })
      );
  }

  public reset() {
    this.store.reset();
  }

  private updateUIState(ui: InvoiceStatusFilter) {
    this.store.update(() => ({
      ui,
    }));
  }
}

export const invoiceStatusService = new InvoiceStatusService(
  new CorporateAccountApi(),
  invoiceStatusStore
);
